'use client';

import React, {useEffect, useState, useRef, useCallback} from "react";
import {USER} from "@/services/Api";
import {toast} from "react-toastify";
import ErrorHandler from "@/components/utils/ErrorHandler";
import {AxiosError} from "axios";
import {useAppSelector} from "@/redux/hooks";
import ShareIcon from "@/components/icons/ShareIcon";
import LikeIcon from "@/components/icons/LikeIcon";
import {motion, AnimatePresence} from "framer-motion";
import {ClipboardDocumentListIcon} from "@heroicons/react/24/outline";

interface Props {
    product: string;
    onFavoriteChanged?: () => void;
    is_favorite?: boolean;
}

const ShareLikeBox = ({product, onFavoriteChanged, is_favorite}: Props) => {
    const [isFavorite, setIsFavorite] = useState(is_favorite);
    const [error, setError] = useState<AxiosError | null>(null);
    const {isLoggedIn} = useAppSelector((state) => state.auth);

    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const linkCopied = `${window.location.origin}${product}`;

    useEffect(() => {
        const hash = window.location.hash; // e.g., "#favorites"
        const lastPart = hash.replace('#', ''); // remove '#' character
        if (lastPart == "favorites") setIsFavorite(true);
        if (!isLoggedIn) setIsFavorite(false);
    }, [isLoggedIn]);

    const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        const url = `${window.location.origin}${product}`;
        navigator.clipboard.writeText(url)
            .then(() => {
                toast.info("لینک کپی شد!")
            })
            .catch(err => {
                console.error("❌ خطا در کپی:", err);
                setError(err);
            });
    };

    const addToFavorite = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        let data = {};
        try {
            const regex = /^\/(product|package)\/(\d+)$/;
            const match = product.match(regex);

            if (match) {
                const type = match[1];
                const id = match[2];

                data = {[type]: parseInt(id)};
            } else {
                toast.error("خطای شناسایی محصول");
            }

            // Call API to add to favorites
            const response = await USER.FAVORITE.add(data);

            if (response.status === 200) {
                setIsFavorite(false);
            }
            if (response.status === 201) {
                setIsFavorite(true);
            }
            const message = response.data.detail;
            toast.success(message);

            // صدا زدن تابع برای بروزرسانی علاقه‌مندی‌ها
            onFavoriteChanged && onFavoriteChanged();

        } catch (e) {
            if (e instanceof AxiosError) {
                setError(e);
            } else {
                setError(null);
            }
            console.error("Unexpected error:", e);
        } finally {
            // Cleanup or additional logic if necessary
        }
    };

    const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsOpen((prev) => !prev);
    };


    const handleShareClick = (e: React.MouseEvent<HTMLButtonElement>, platform: "telegram" | "whatsapp") => {
        e.preventDefault();
        // navigator.clipboard.writeText(linkCopied)
        //     .then(() => toast.info("لینک کپی شد!"))
        //     .catch(() => {
        //     });
        if (platform === "telegram") {
            window.open(`https://t.me/share/url?url=${encodeURIComponent(linkCopied)}`, "_blank");
        } else if (platform === "whatsapp") {
            window.open(`https://wa.me/?text=${encodeURIComponent(linkCopied)}`, "_blank");
        }
        setIsOpen(false);
    };

    const handleClickOutside = useCallback((e: MouseEvent) => {
        if (
            menuRef.current && !menuRef.current.contains(e.target as Node) &&
            buttonRef.current && !buttonRef.current.contains(e.target as Node)
        ) {
            setIsOpen(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, handleClickOutside]);

    return (
        <div className="flex w-full overflow-hidden">
            <ErrorHandler error={error}/>

            <div className="w-1/2">
                <button
                    ref={buttonRef}
                    className="flex w-full h-full items-center justify-center"
                    onClick={toggleMenu}
                >
                    <ShareIcon className="size-4 fill-gray-600 hover:fill-primary hover:scale-105 cursor-pointer"/>
                </button>

                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            ref={menuRef}
                            initial={{opacity: 0, y: -5}}
                            animate={{opacity: 1, y: 0}}
                            exit={{opacity: 0, y: -5}}
                            className="absolute bottom-16 mt-2 left-4 w-auto bg-white border rounded-sm shadow-lg z-50 p-2 flex flex-row gap-2"
                        >
                            <button
                                className="flex items-center gap-1 p-2 text-gray-700 hover:bg-gray-100"
                                onClick={handleCopy}
                            >
                                <ClipboardDocumentListIcon className="size-5" title="کپی لینک"/>
                            </button>
                            <button
                                className="flex items-center gap-1 p-2 hover:bg-gray-100"
                                onClick={(e) => handleShareClick(e, "telegram")}
                            >
                                <img src="/images/socialMedia/telegram.svg" alt="Telegram" className="size-5"/>
                            </button>
                            <button
                                className="flex items-center gap-1 p-2 hover:bg-gray-100 rounded"
                                onClick={(e) => handleShareClick(e, "whatsapp")}
                            >
                                <img src="/images/socialMedia/whatsapp.svg" alt="WhatsApp" className="size-5"/>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

            </div>

            <div className="h-10 w-[1px] bg-gray-500"/>

            <button onClick={addToFavorite} className="flex items-center justify-center w-1/2">
                <LikeIcon
                    isFavorite={isFavorite}
                    className="size-4 cursor-pointer"
                />
            </button>
        </div>
    );

}

export default ShareLikeBox;